import { createFileRoute } from '@tanstack/react-router';
import { Check, Server, Users } from 'lucide-react';

import { PageHeader } from '@/components/layout/PageHeader';
import { RequireCapability } from '@/components/layout/RequireCapability';
import { StateChip } from '@/components/primitives/StateChip';
import { ErrorState } from '@/components/states/ErrorState';
import { Skeleton } from '@/components/states/Skeleton';
import { StateBlock } from '@/components/states/StateBlock';
import {
  useCapabilityMatrix,
  useEnvironmentInfo,
  useOperators,
} from '@/features/settings/settings';
import { ROLE_LABELS, type AdminRole } from '@/lib/permissions';
import { Construction } from 'lucide-react';

export const Route = createFileRoute('/settings')({
  component: SettingsScreen,
});

const ROLE_ORDER: AdminRole[] = ['owner', 'engineer', 'content', 'moderator'];

function SettingsScreen() {
  return (
    <RequireCapability capability="settings.view">
      <div className="flex flex-col gap-4">
        <PageHeader
          eyebrow="Governance"
          title="Settings"
          meta="Operators, the capability matrix, and what this deployment is pointed at"
        />

        <OperatorsPanel />
        <CapabilityMatrixPanel />
        <EnvironmentPanel />
        <ManagementPanel />
      </div>
    </RequireCapability>
  );
}

/* --------------------------------------------------------------- operators */

function OperatorsPanel() {
  const { data, isPending, isError, error, refetch } = useOperators();

  return (
    <Panel title="Operators" icon={Users} count={data?.operators.length}>
      {isError ? (
        <ErrorState error={error} subject="operators" onRetry={() => void refetch()} />
      ) : isPending ? (
        <Skeleton height={120} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-max border-collapse text-left">
            <caption className="sr-only">Provisioned admin console operators</caption>
            <thead>
              <tr className="border-bd border-b">
                {['Name', 'Email', 'Role', 'Last signed in', 'Status'].map((heading) => (
                  <th
                    key={heading}
                    scope="col"
                    className="font-head text-tx3 py-1.5 pr-4 text-[10.5px] font-semibold uppercase"
                    style={{ letterSpacing: '0.14em' }}
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.operators.map((operator) => (
                <tr key={operator.id} className="border-bd/60 border-b last:border-b-0">
                  <td className="text-tx py-2 pr-4 text-[13px]">{operator.name}</td>
                  <td className="mesh-mono text-tx2 py-2 pr-4 text-[12px]">{operator.email}</td>
                  <td className="py-2 pr-4">
                    <StateChip
                      state={operator.role}
                      size="sm"
                      label={ROLE_LABELS[operator.role]}
                      tone={operator.role === 'owner' ? 'warn' : 'neutral'}
                    />
                  </td>
                  <td className="mesh-mono text-tx3 py-2 pr-4 text-[11.5px]">
                    {operator.lastSignedInAt
                      ? operator.lastSignedInAt.replace('T', ' ').slice(0, 16)
                      : 'never'}
                  </td>
                  <td className="py-2 pr-4">
                    <StateChip
                      state={operator.disabledAt ? 'disabled' : 'active'}
                      size="sm"
                      tone={operator.disabledAt ? 'muted' : 'good'}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Explains why a disabled operator is listed rather than hidden. */}
          <p className="text-tx3 mt-2 text-[11.5px]">
            Access is revoked by disabling, not deleting, so historical audit rows still resolve
            to a name. Signing in with a permitted Google Workspace account is not enough on its
            own — a row here is what grants authorisation.
          </p>
        </div>
      )}
    </Panel>
  );
}

/* -------------------------------------------------------- capability matrix */

function CapabilityMatrixPanel() {
  const { data, isPending, isError, error, refetch } = useCapabilityMatrix();

  return (
    <Panel title="Capability matrix">
      {isError ? (
        <ErrorState
          error={error}
          subject="the capability matrix"
          onRetry={() => void refetch()}
        />
      ) : isPending ? (
        <Skeleton height={220} />
      ) : (
        <>
          <p className="text-tx3 mb-2.5 text-[11.5px]">
            Served by mesh-admin-api, which is the authority. The console carries a mirrored
            copy so it can hide controls, but only this one is enforced.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full min-w-max border-collapse text-left">
              <caption className="sr-only">Capabilities granted to each operator role</caption>
              <thead>
                <tr className="border-bd border-b">
                  <th
                    scope="col"
                    className="font-head text-tx3 py-1.5 pr-4 text-[10.5px] font-semibold uppercase"
                    style={{ letterSpacing: '0.14em' }}
                  >
                    Capability
                  </th>
                  {ROLE_ORDER.map((role) => (
                    <th
                      key={role}
                      scope="col"
                      className="font-head text-tx3 px-2 py-1.5 text-center text-[10.5px] font-semibold uppercase"
                      style={{ letterSpacing: '0.14em' }}
                    >
                      {ROLE_LABELS[role]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.domains.map((domain) => (
                  <DomainRows
                    key={domain}
                    domain={domain}
                    capabilities={data.allCapabilities.filter((capability) =>
                      capability.startsWith(`${domain}.`),
                    )}
                    byRole={
                      new Map(data.roles.map((entry) => [entry.role, entry.capabilities]))
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Panel>
  );
}

function DomainRows({
  domain,
  capabilities,
  byRole,
}: {
  domain: string;
  capabilities: string[];
  byRole: Map<AdminRole, string[]>;
}) {
  return (
    <>
      <tr>
        <th
          scope="rowgroup"
          colSpan={ROLE_ORDER.length + 1}
          className="font-head text-tx2 pt-3 pb-1 text-left text-[10px] font-semibold uppercase"
          style={{ letterSpacing: '0.16em' }}
        >
          {domain}
        </th>
      </tr>
      {capabilities.map((capability) => (
        <tr key={capability} className="border-bd/60 border-b last:border-b-0">
          <td className="mesh-mono text-tx2 py-1.5 pr-4 text-[11.5px]">{capability}</td>
          {ROLE_ORDER.map((role) => {
            const granted = byRole.get(role)?.includes(capability) ?? false;
            return (
              <td key={role} className="px-2 py-1.5 text-center">
                {granted ? (
                  <Check
                    size={13}
                    aria-label="granted"
                    className="inline"
                    style={{ color: 'var(--ok)' }}
                  />
                ) : (
                  <span aria-label="not granted" className="text-tx3 text-[11px]">
                    ·
                  </span>
                )}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

/* ------------------------------------------------------------- environment */

function EnvironmentPanel() {
  const { data, isPending, isError, error, refetch } = useEnvironmentInfo();

  return (
    <Panel title="This deployment" icon={Server}>
      {isError ? (
        <ErrorState error={error} subject="environment info" onRetry={() => void refetch()} />
      ) : isPending ? (
        <Skeleton height={80} />
      ) : (
        <>
          <dl className="grid gap-x-4 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
            <MetaField label="Environment" value={data.meshEnv} />
            <MetaField label="Console origin" value={data.adminUiOrigin} />
            <MetaField label="ZazzBackend" value={data.zazzBackendUrl} />
            <MetaField label="Mongo host" value={data.mongoHost ?? '—'} />
            <MetaField label="Redis host" value={data.redisHost ?? '—'} />
          </dl>
          {/* Says plainly why there is no "is the service token working?" indicator. */}
          <p className="text-tx3 mt-2.5 text-[11.5px]">
            Hosts only — connection strings carry credentials and are never sent to the browser.
            Whether ZazzBackend accepts this deployment's service token cannot be answered from
            here; a real delegated call failing is the only honest signal, and those show up in
            the audit log.
          </p>
        </>
      )}
    </Panel>
  );
}

/* -------------------------------------------------------------- management */

/**
 * No controls, deliberately. Adding, disabling or re-roling an operator changes who
 * can take Tier 3 actions, so it is itself a Tier 3 surface and needs the full
 * confirmation choreography plus audit rows — none of which has an endpoint yet.
 * Provisioning is a direct database write today.
 */
function ManagementPanel() {
  return (
    <Panel title="Operator management">
      <StateBlock
        icon={Construction}
        tone="info"
        title="Read-only for now"
        description="Adding, disabling and re-roling operators is not wired. It changes who can take Tier 3 actions, so it is a Tier 3 surface itself and needs its own confirmation flow and audit rows. Provisioning is a direct write to admin_users today."
      />
    </Panel>
  );
}

/* ------------------------------------------------------------------- shell */

function MetaField({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt
        className="font-head text-tx3 text-[10px] font-semibold uppercase"
        style={{ letterSpacing: '0.14em' }}
      >
        {label}
      </dt>
      <dd className="mesh-mono text-tx2 mt-0.5 truncate text-[12px]" title={value}>
        {value}
      </dd>
    </div>
  );
}

function Panel({
  title,
  icon: Icon,
  count,
  children,
}: {
  title: string;
  icon?: typeof Users;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <section className="border-bd bg-card rounded-xl border p-3.5">
      <h2
        className="font-head text-tx2 mb-2.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase"
        style={{ letterSpacing: '0.16em' }}
      >
        {Icon && <Icon size={12} aria-hidden />}
        {title}
        {count !== undefined && <span className="mesh-mono text-tx3 text-[11px]">{count}</span>}
      </h2>
      {children}
    </section>
  );
}
